// import { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Shield, Eye, EyeOff, Lock, AlertCircle, ArrowRight } from "lucide-react";
// import { apiClient } from "@/domain/api";
// import { useAuthStore, buildProfileFromUser, type AuthUser } from "@/domain/authStore";

// type LoginResponse = AuthUser & { token: string };

// const TRUST_PILLARS = [
//   {
//     icon: Shield,
//     label: "Transparent",
//     body: "Real-time spend visibility from funder to last mile",
//     color: "text-[#535EFF]",
//     bg: "bg-[#535EFF]/10 ring-[#535EFF]/20",
//   },
//   {
//     icon: Eye,
//     label: "Verified",
//     body: "AI-assisted evidence matching and site-visit capture",
//     color: "text-[#39D559]",
//     bg: "bg-[#39D559]/10 ring-[#39D559]/20",
//   },
//   {
//     icon: Lock,
//     label: "Accountable",
//     body: "Immutable audit trail with authority-grant controls",
//     color: "text-[#C9A24B]",
//     bg: "bg-[#C9A24B]/10 ring-[#C9A24B]/20",
//   },
// ];


// export function LoginPage() {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [loading, setLoading] = useState(false);

//   const login = useAuthStore((s) => s.login);
//   const navigate = useNavigate();

//   async function handleSubmit(e: React.FormEvent) {
//     e.preventDefault();
//     setError(null);
//     setLoading(true);
//     try {
//       const { data } = await apiClient.post<LoginResponse>("/auth/login", { email, password });
//       login(data, data.token);
//       navigate(buildProfileFromUser(data).landing, { replace: true });
//     } catch (err: unknown) {
//       const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
//       setError(msg ?? "Invalid email or password");
//     } finally {
//       setLoading(false);
//     }
//   }

//   return (
//     <div className="min-h-screen flex">
//       {/* ── Left: brand panel ──────────────────────────────────────────────── */}
//       <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-col justify-between p-12 bg-[#0A0E22] relative overflow-hidden flex-shrink-0">
//         {/* Subtle dot grid */}
//         <div
//           className="absolute inset-0 opacity-[0.04]"
//           style={{
//             backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)",
//             backgroundSize: "28px 28px",
//           }}
//         />
//         {/* Glow blobs */}
//         <div className="pointer-events-none absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full bg-[#272FA0] opacity-[0.22] blur-3xl" />
//         <div className="pointer-events-none absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#535EFF] opacity-[0.14] blur-3xl translate-x-1/3 translate-y-1/3" />

//         {/* Logo */}
//         <div className="relative flex items-center gap-3">
//           <ILLogoMark className="w-9 h-9" />
//           <span className="text-white font-semibold text-lg tracking-tight">Impact Ledger</span>
//         </div>

//         {/* Headline, stats, pillars */}
//         <div className="relative space-y-10">
//           <div>
//             <h1 className="text-[2.75rem] font-bold text-white leading-[1.12] tracking-[-0.03em]">
//               Verified impact.<br />
//               <span className="text-[#535EFF]">Fully accounted for.</span>
//             </h1>
//             <p className="mt-4 text-sm leading-relaxed text-white/50 max-w-xs">
//               Every disbursement, evidence upload, and site visit recorded on a tamper-evident ledger.
//               Oversight that funders and governments can trust.
//             </p>
//           </div>

//           <div className="grid grid-cols-3 gap-4">
//             {TRUST_PILLARS.map(({ icon: Icon, label, body, color, bg }) => (
//               <div key={label}>
//                 <div className={`w-9 h-9 rounded-xl ring-1 flex items-center justify-center ${bg}`}>
//                   <Icon className={`w-4 h-4 ${color}`} />
//                 </div>
//                 <p className="mt-2.5 text-sm font-semibold text-white/90">{label}</p>
//                 <p className="mt-1 text-[11px] text-white/38 leading-relaxed">{body}</p>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Footer badge */}
//         <div className="relative flex items-center gap-2">
//           <span className="w-1.5 h-1.5 rounded-full bg-[#39D559] shadow-[0_0_6px_#39D559]" />
//           <span className="text-xs text-white/22 tracking-wide">
//             Philanthrify · Uganda Fire Stations Programme 2026
//           </span>
//         </div>
//       </div>

//       {/* ── Right: form panel ──────────────────────────────────────────────── */}
//       <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-[#F8F9FA] to-[#EEF2FF]">
//         {/* Mobile logo */}
//         <div className="lg:hidden mb-10 flex items-center gap-2.5">
//           <ILLogoMark className="w-8 h-8" />
//           <span className="text-[#0A0E22] font-semibold text-lg tracking-tight">Impact Ledger</span>
//         </div>

//         {/* Form card */}
//         <div className="w-full max-w-[380px] bg-white rounded-2xl border border-[#E5E8EE] shadow-sm shadow-[#0F1129]/[0.06] p-8">
//           <div className="mb-7">
//             <h2 className="text-2xl font-bold text-[#0F1129] tracking-tight">Sign in</h2>
//             <p className="mt-1.5 text-sm text-[#64748B]">Access your workspace</p>
//           </div>

//           <form onSubmit={handleSubmit} className="space-y-5">
//             <div>
//               <label htmlFor="email" className="block text-sm font-semibold text-[#1A1A2E] mb-1.5">
//                 Email address
//               </label>
//               <input
//                 id="email"
//                 type="email"
//                 autoComplete="email"
//                 required
//                 value={email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 placeholder="you@organisation.com"
//                 className="w-full bg-[#F8F9FA] border border-[#D1D5DB] rounded-lg px-3.5 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] focus:outline-none focus:bg-white focus:border-[#272FA0] focus:ring-2 focus:ring-[#272FA0]/25 transition-all"
//               />
//             </div>

//             <div>
//               <label htmlFor="password" className="block text-sm font-semibold text-[#1A1A2E] mb-1.5">
//                 Password
//               </label>
//               <div className="relative">
//                 <input
//                   id="password"
//                   type={showPassword ? "text" : "password"}
//                   autoComplete="current-password"
//                   required
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   placeholder="••••••••"
//                   className="w-full bg-[#F8F9FA] border border-[#D1D5DB] rounded-lg px-3.5 py-3 pr-10 text-sm text-[#111827] placeholder-[#9CA3AF] focus:outline-none focus:bg-white focus:border-[#272FA0] focus:ring-2 focus:ring-[#272FA0]/25 transition-all"
//                 />
//                 <button
//                   type="button"
//                   onClick={() => setShowPassword((v) => !v)}
//                   aria-label={showPassword ? "Hide password" : "Show password"}
//                   className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
//                 >
//                   {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
//                 </button>
//               </div>
//             </div>

//             {error && (
//               <div className="flex items-start gap-2.5 rounded-lg bg-red-50 border border-red-200 px-3.5 py-2.5">
//                 <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
//                 <p className="text-sm text-red-700">{error}</p>
//               </div>
//             )}

//             <button
//               type="submit"
//               disabled={loading}
//               className="group w-full bg-[#272FA0] hover:bg-[#1F2680] text-white rounded-lg px-4 py-3 text-sm font-bold tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm flex items-center justify-center gap-2"
//             >
//               {loading ? (
//                 <>
//                   <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
//                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
//                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
//                   </svg>
//                   Signing in…
//                 </>
//               ) : (
//                 <>
//                   Sign in
//                   <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150" />
//                 </>
//               )}
//             </button>
//           </form>

//           <div className="mt-6 pt-5 border-t border-[#F1F3F7] flex items-center justify-center gap-1.5">
//             <Lock className="w-3 h-3 text-[#94A3B8]" />
//             <p className="text-xs text-[#94A3B8]">Access is by invitation only</p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// function ILLogoMark({ className }: { className?: string }) {
//   return (
//     <div className={`rounded-lg bg-[#272FA0] ring-1 ring-white/10 flex items-center justify-center ${className}`}>
//       <svg viewBox="0 0 16 16" fill="none" className="w-[45%] h-[45%] text-white">
//         <path
//           d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z"
//           stroke="currentColor"
//           strokeWidth="1.5"
//           strokeLinejoin="round"
//         />
//       </svg>
//     </div>
//   );
// }



import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Eye, EyeOff, Lock, AlertCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { apiClient } from "@/domain/api";
import { useAuthStore, buildProfileFromUser, type AuthUser } from "@/domain/authStore";
import { useStore } from "@/domain/store";
import { resolveMyHomeWorkspaceId } from "@/domain/homeWorkspace";

type LoginResponse = AuthUser & { token: string };

const TRUST_PILLARS = [
  {
    icon: Shield,
    label: "Transparent",
    body: "Real-time spend visibility from funder to last mile",
    color: "text-[#535EFF]",
    bg: "bg-[#535EFF]/10 ring-[#535EFF]/20",
  },
  {
    icon: CheckCircle2,
    label: "Verified",
    body: "AI-assisted evidence matching and site-visit capture",
    color: "text-[#39D559]",
    bg: "bg-[#39D559]/10 ring-[#39D559]/20",
  },
  {
    icon: Lock,
    label: "Accountable",
    body: "Immutable audit trail with authority-grant controls",
    color: "text-[#C9A24B]",
    bg: "bg-[#C9A24B]/10 ring-[#C9A24B]/20",
  },
];

const STATS = [
  { value: "98.7%", label: "Funds tracked" },
  { value: "2.4s", label: "Avg. verification" },
  { value: "100%", label: "Audit coverage" },
];

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const login = useAuthStore((s) => s.login);
  const setDevMode = useStore((s) => s.setDevMode);
  const setWorkspace = useStore((s) => s.setWorkspace);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await apiClient.post<LoginResponse>("/auth/login", { email, password });
      login(data, data.token);
      // A real credentialed login is an explicit "show me my own account" signal
      // that must win over a stale "Demo mode" flag left on from unrelated
      // testing on this device - useActiveProfile() gives devMode priority over
      // a real authUser by design (so a developer can walk every persona while
      // also logged in), but that same precedence silently hijacks a genuine
      // end user's login into fake seed data with zero indication anything is
      // wrong. Concretely: this broke a real reporter's raise-ask project list
      // and inbox, both landing empty against demo data from a workspace he'd
      // never touched, purely because devMode was left on from an earlier
      // session on the same browser.
      setDevMode(false);
      // Pin the active workspace to wherever this user's own real project(s)
      // actually are - otherwise it's just whatever any persona last browsed
      // on this device, real or demo, and a reporter/contractor whose real
      // project lives in workspace A silently sees an empty app if the
      // browser was last left on workspace B. No-op (resolves null, leaves
      // the workspace untouched) for oversight/funder users who range across
      // workspaces by design, or if this can't be determined - never blocks
      // login on it.
      const homeWorkspaceId = await resolveMyHomeWorkspaceId();
      if (homeWorkspaceId) setWorkspace(homeWorkspaceId);
      // ...and land on THAT workspace's URL, not the persona default. The
      // route's :workspaceId param - not the store - is what scopes every
      // project list on the page, so navigating to the hardcoded
      // "/workspace/uganda-fire-stations" in USERTYPE_MAP put a contractor
      // whose project lives in another programme on the wrong workspace,
      // undoing the pin above. Only rewrite workspace landings; /stratus,
      // /funder and /reporter are not workspace-scoped.
      const profileLanding = buildProfileFromUser(data).landing;
      const landing =
        homeWorkspaceId && profileLanding.startsWith("/workspace/")
          ? `/workspace/${homeWorkspaceId}`
          : profileLanding;
      navigate(landing, { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? "Invalid email or password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Left: brand panel ──────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[500px] xl:w-[580px] flex-col justify-between p-12 bg-gradient-to-br from-[#0A0E22] via-[#0F1335] to-[#1A1F3A] relative overflow-hidden flex-shrink-0">
        {/* Animated gradient orbs */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-[#272FA0] opacity-20 blur-[120px] animate-pulse" 
               style={{ animationDuration: "8s" }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#535EFF] opacity-15 blur-[100px] translate-x-1/4 translate-y-1/4 animate-pulse" 
               style={{ animationDuration: "6s", animationDelay: "1s" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-[#39D559] opacity-10 blur-[80px] animate-pulse"
               style={{ animationDuration: "10s", animationDelay: "2s" }} />
        </div>

        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Logo */}
        <div className="relative flex items-center gap-3 z-10">
          <ILLogoMark className="w-10 h-10" />
          <div>
            <span className="block text-white font-bold text-xl tracking-tight">Impact Ledger</span>
            <span className="block text-white/40 text-[10px] uppercase tracking-widest font-semibold mt-0.5">
              Verified Impact Platform
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="relative space-y-12 z-10">
          {/* Headline */}
          <div>
            <h1 className="text-[3rem] font-bold text-white leading-[1.1] tracking-[-0.04em]">
              Verified impact.
              <br />
              <span className="bg-gradient-to-r from-[#535EFF] to-[#39D559] bg-clip-text text-transparent">
                Fully accounted for.
              </span>
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/60 max-w-md">
              Every disbursement, evidence upload, and site visit recorded on a tamper-evident ledger.
              Oversight that funders and governments can trust.
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-8">
            {STATS.map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold text-white tabular-nums">{value}</p>
                <p className="mt-1 text-xs text-white/40 uppercase tracking-wider font-semibold">{label}</p>
              </div>
            ))}
          </div>

          {/* Trust pillars */}
          <div className="grid grid-cols-3 gap-5">
            {TRUST_PILLARS.map(({ icon: Icon, label, body, color, bg }) => (
              <div key={label} className="group">
                <div className={`w-11 h-11 rounded-xl ring-1 flex items-center justify-center ${bg} transition-transform group-hover:scale-110 duration-300`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <p className="mt-3 text-sm font-bold text-white/95">{label}</p>
                <p className="mt-1.5 text-xs text-white/45 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer badge */}
        <div className="relative flex items-center gap-2.5 z-10">
          <div className="relative">
            <span className="absolute inset-0 w-2 h-2 rounded-full bg-[#39D559] animate-ping opacity-75" />
            <span className="relative block w-2 h-2 rounded-full bg-[#39D559] shadow-[0_0_8px_#39D559]" />
          </div>
          <span className="text-xs text-white/30 tracking-wide">
            Philanthrify · Uganda Fire Stations Programme 2026
          </span>
        </div>
      </div>

      {/* ── Right: form panel ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-[#FAFBFC] via-white to-[#F5F7FA]">
        {/* Mobile logo */}
        <div className="lg:hidden mb-12 flex flex-col items-center gap-3">
          <ILLogoMark className="w-12 h-12" />
          <div className="text-center">
            <span className="block text-[#0A0E22] font-bold text-xl tracking-tight">Impact Ledger</span>
            <span className="block text-[#64748B] text-xs uppercase tracking-widest font-semibold mt-1">
              Verified Impact Platform
            </span>
          </div>
        </div>

        {/* Form card */}
        <div className="w-full max-w-[420px] bg-white rounded-2xl border border-[#E5E8EE] shadow-xl shadow-[#0F1129]/[0.08] p-10">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0F1129] tracking-tight">Welcome back</h2>
            <p className="mt-2 text-sm text-[#64748B]">Sign in to access your workspace</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-[#1A1A2E] mb-2">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organisation.com"
                className="w-full bg-[#F8F9FA] border border-[#D1D5DB] rounded-xl px-4 py-3.5 text-sm text-[#111827] placeholder-[#9CA3AF] focus:outline-none focus:bg-white focus:border-[#272FA0] focus:ring-4 focus:ring-[#272FA0]/10 transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-[#1A1A2E] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#F8F9FA] border border-[#D1D5DB] rounded-xl px-4 py-3.5 pr-12 text-sm text-[#111827] placeholder-[#9CA3AF] focus:outline-none focus:bg-white focus:border-[#272FA0] focus:ring-4 focus:ring-[#272FA0]/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#272FA0] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3.5">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full bg-gradient-to-r from-[#272FA0] to-[#1F2680] hover:from-[#1F2680] hover:to-[#181F65] text-white rounded-xl px-4 py-4 text-sm font-bold tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-[#272FA0]/25 hover:shadow-xl hover:shadow-[#272FA0]/30 flex items-center justify-center gap-2 overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              {loading ? (
                <>
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Signing in…
                </>
              ) : (
                <>
                  Sign in to workspace
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#F1F3F7] flex items-center justify-center gap-2">
            <Lock className="w-3.5 h-3.5 text-[#94A3B8]" />
            <p className="text-xs text-[#94A3B8] font-medium">Access is by invitation only</p>
          </div>
        </div>

        {/* Bottom tagline */}
        <p className="mt-8 text-xs text-[#94A3B8] text-center max-w-md">
          Trusted by development organizations worldwide for transparent, accountable impact delivery
        </p>
      </div>
    </div>
  );
}

function ILLogoMark({ className }: { className?: string }) {
  return (
    <div className={`rounded-xl bg-gradient-to-br from-[#272FA0] to-[#535EFF] ring-1 ring-white/15 shadow-lg shadow-[#272FA0]/25 flex items-center justify-center ${className}`}>
      <svg viewBox="0 0 16 16" fill="none" className="w-[50%] h-[50%] text-white">
        <path
          d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          fill="currentColor"
          fillOpacity="0.1"
        />
      </svg>
    </div>
  );
}